import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { describeGroups } from '../search/searchTypes';
import { formatDate } from '../utils/dateFormat';

const MIN_LENGTH = 2;
const DELAY_MS = 250;

/**
 * The search box in the top bar: type a few words (a name, a bill or order number, a phone or GSTIN) and pick from what matches, grouped by
 * kind. The server gives each person only the kinds their role may open (GlobalSearchService); search/searchTypes.js words them and says
 * where each goes. Ctrl/Cmd+K focuses it, arrows move, Enter opens, Esc closes.
 */
function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [asked, setAsked] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const input = useRef(null);
  const latest = useRef(0);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // The words settle for a moment before they are searched; an older answer never replaces a newer one
  useEffect(() => {
    const q = text.trim();
    if (q.length < MIN_LENGTH) {
      setGroups([]);
      setAsked('');
      setLoading(false);
      return undefined;
    }
    const mine = ++latest.current;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get('/api/search', { params: { q } })
        .then((r) => { if (mine === latest.current) { setGroups(describeGroups(r.data?.groups, user)); setAsked(q); setActive(0); } })
        .catch(() => { if (mine === latest.current) { setGroups([]); setAsked(q); } })
        .finally(() => { if (mine === latest.current) setLoading(false); });
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [text, user]);

  // Ctrl/Cmd+K from anywhere
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // A click anywhere else closes the list
  useEffect(() => {
    const onDown = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const go = useCallback((item) => {
    setOpen(false);
    setText('');
    setGroups([]);
    input.current?.blur();
    navigate(item.to);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); input.current?.blur(); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % flat.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + flat.length) % flat.length); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[Math.min(active, flat.length - 1)]); }
  };

  if (!user) return null;
  const searched = text.trim().length >= MIN_LENGTH;
  let index = -1;

  return (
    <div className="topbar-search" ref={box} role="search">
      <i className="bi bi-search topbar-search-icon" aria-hidden="true" />
      <input
        ref={input}
        type="search"
        className="form-control form-control-sm topbar-search-input"
        placeholder="Search customers, bills, orders, items…  (Ctrl K)"
        aria-label="Search"
        autoComplete="off"
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {loading && <Spinner animation="border" size="sm" className="topbar-search-spinner" />}
      {open && searched && (
        <div className="topbar-search-results" role="listbox">
          {!loading && groups.length === 0 && asked === text.trim() && (
            <div className="topbar-search-empty">Nothing found for &ldquo;{asked}&rdquo;. Try a name, a number or fewer words.</div>
          )}
          {groups.map((g) => (
            <div key={g.type}>
              <div className="topbar-search-group"><i className={`bi ${g.icon} me-1`} aria-hidden="true" />{g.label}</div>
              {g.items.map((item) => {
                index += 1;
                const mine = index;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="option"
                    aria-selected={mine === active}
                    className={`topbar-search-item${mine === active ? ' active' : ''}`}
                    onMouseEnter={() => setActive(mine)}
                    onClick={() => go(item)}
                  >
                    <span className="topbar-search-main">
                      <span className="fw-semibold">{item.title}</span>
                      {item.subtitle && <span className="text-muted small ms-2">{item.subtitle}</span>}
                    </span>
                    <span className="topbar-search-meta text-muted small">
                      {item.date ? formatDate(item.date) : ''}
                      {item.status ? `${item.date ? ' · ' : ''}${item.status.toLowerCase()}` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
